import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { DateRangeFilter } from './filters/DateRangeFilter';
import { MultiSelectFilter } from './filters/MultiSelectFilter';
import type { ReportParams, Filter } from '../types/widget';
import { MargeBrute } from '../tabs/MargeBrute';
import { MargeBruteAlia } from '../tabs/MargeBruteAlia';
import { SuiviReglements } from '../tabs/SuiviReglements';
import { DelaisReglement } from '../tabs/DelaisReglement';
import { DelaisEnregistrement } from '../tabs/DelaisEnregistrement';
import { DelaisTraitement } from '../tabs/DelaisTraitement';
import { Refacturation } from '../tabs/Refacturation';
import { ConnectorProvider, readAuthConnectors } from '../contexts/ConnectorContext';

interface TabDef {
    id: string;
    label: string;
    /** Sous-chemin sous /:connectorSlug — ex: 'marge-brute' ou 'suivi-reglements/client'. */
    relPath: string;
    feature?: string;
    subTabs?: { id: string; label: string; relPath: string }[];
}

const allTabs: TabDef[] = [
    { id: 'marge-brute', label: 'Marge brute', relPath: 'marge-brute', feature: 'gross_margin' },
    {
        id: 'suivi-reglements',
        label: 'Suivi des règlements',
        relPath: 'suivi-reglements/client',
        subTabs: [
            { id: 'client', label: 'Client', relPath: 'suivi-reglements/client' },
            { id: 'prestataire', label: 'Prestataire', relPath: 'suivi-reglements/prestataire' },
        ],
    },
    {
        id: 'delais-reglement',
        label: 'Délais de règlement',
        relPath: 'delais-reglement/client',
        subTabs: [
            { id: 'client', label: 'Client', relPath: 'delais-reglement/client' },
            { id: 'prestataire', label: 'Prestataire', relPath: 'delais-reglement/prestataire' },
        ],
    },
    { id: 'delais-enregistrement', label: "Délais d'enregistrement", relPath: 'delais-enregistrement' },
    { id: 'delais-traitement', label: 'Délais de traitement', relPath: 'delais-traitement' },
    { id: 'refacturation', label: 'Refacturation', relPath: 'refacturation', feature: 'refacturation' },
];

function getDefaultDates(): { start: string; end: string } {
    const now = new Date();
    const year = now.getFullYear();
    return {
        start: `${year}-01-01`,
        end: now.toISOString().slice(0, 10),
    };
}

export function ConnectorPage() {
    const { connectorSlug } = useParams<{ connectorSlug: string }>();
    const connectors = readAuthConnectors();
    const activeConnector = connectors.find((c) => c.slug === connectorSlug);

    if (connectors.length === 0) {
        return (
            <div className="p-6 text-sm text-gray-600">
                Aucune société accessible. Contactez un administrateur.
            </div>
        );
    }

    if (!activeConnector) {
        return <Navigate to={`/${connectors[0].slug}/marge-brute`} replace />;
    }

    return (
        <ConnectorProvider activeConnector={activeConnector} connectors={connectors}>
            <ConnectorPageContent slug={activeConnector.slug} connectorId={activeConnector.id} />
        </ConnectorProvider>
    );
}

function ConnectorPageContent({ slug, connectorId }: { slug: string; connectorId: number }) {
    const navigate = useNavigate();
    const location = useLocation();

    const defaults = useMemo(() => getDefaultDates(), []);
    const [dateFilters, setDateFilters] = useState<Filter[]>([]);
    const [selectedSuppliers, setSelectedSuppliers] = useState<(number | string)[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<(number | string)[]>([]);
    const [selectedCustomerTiers, setSelectedCustomerTiers] = useState<(number | string)[]>([]);
    const [features, setFeatures] = useState<Record<string, boolean>>({});
    const [driver, setDriver] = useState<string | null>(null);
    const [degraded, setDegraded] = useState(false);

    // Reset filters when changing connector
    useEffect(() => {
        setDateFilters([]);
        setSelectedSuppliers([]);
        setSelectedCustomers([]);
        setSelectedCustomerTiers([]);
    }, [slug]);

    useEffect(() => {
        // Reset driver/features avant le re-fetch pour ne pas afficher l'ancien
        // composant marge-brute (ex: MargeBruteAlia) avec des données du nouveau
        // connector pendant la latence réseau du /api/emis/config.
        setDriver(null);
        setFeatures({});

        const token = localStorage.getItem('auth_token');
        if (!token) return;
        fetch('/api/emis/config', {
            headers: {
                Authorization: `Bearer ${token}`,
                'X-Connector-Id': String(connectorId),
            },
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                setFeatures(data?.features ?? {});
                setDriver(typeof data?.driver === 'string' ? data.driver : null);
                setDegraded(Boolean(data?.degraded));
            })
            .catch(() => {
                setFeatures({});
                setDriver(null);
                setDegraded(false);
            });
    }, [connectorId]);

    const tabs = useMemo(
        () => allTabs.filter((t) => !t.feature || features[t.feature]),
        [features],
    );

    const filters = useMemo<ReportParams>(() => {
        const params: ReportParams = {};
        const df = dateFilters[0];
        if (df && Array.isArray(df.value) && df.value.length === 2) {
            const [startYm, endYm] = df.value as [string, string];
            const [endY, endM] = endYm.split('-').map(Number);
            const lastDay = new Date(endY, endM, 0).getDate();
            params.date_start = `${startYm}-01`;
            params.date_end = `${endYm}-${String(lastDay).padStart(2, '0')}`;
        } else {
            params.date_start = defaults.start;
            params.date_end = defaults.end;
        }
        if (selectedSuppliers.length > 0) {
            params.supplier_ids = selectedSuppliers.map((v) => Number(v));
        }
        if (selectedCustomers.length > 0) {
            params.customer_codes = selectedCustomers.map((v) => String(v));
        }
        if (selectedCustomerTiers.length > 0) {
            params.pennylane_customer_ids = selectedCustomerTiers.map((v) => Number(v));
        }
        return params;
    }, [dateFilters, selectedSuppliers, selectedCustomers, selectedCustomerTiers, defaults]);

    const currentSubPath = location.pathname.replace(new RegExp(`^/${slug}/?`), '');

    const activeTab = tabs.find((t) => {
        if (t.subTabs) {
            return t.subTabs.some((st) => currentSubPath === st.relPath);
        }
        return currentSubPath === t.relPath;
    }) ?? tabs[0];

    const activeSubTab = activeTab?.subTabs?.find((st) => currentSubPath === st.relPath) ?? activeTab?.subTabs?.[0];

    if (!activeTab) {
        return null;
    }

    const tabPath = (relPath: string) => `/${slug}/${relPath}`;

    return (
        <div className="space-y-4">
            {degraded && (
                <div
                    role="status"
                    className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-2 text-sm"
                >
                    Service de configuration ALEO momentanément indisponible — certaines fonctionnalités peuvent être masquées par sécurité. Réessayez dans une minute.
                </div>
            )}

            {/* Filter bar */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-end gap-4">
                <DateRangeFilter
                    label="Période"
                    defaultStart={defaults.start}
                    defaultEnd={defaults.end}
                    onChange={setDateFilters}
                />
                <MultiSelectFilter
                    label="Fournisseur"
                    endpoint="/api/emis/filters/suppliers"
                    selected={selectedSuppliers}
                    onChange={setSelectedSuppliers}
                />
                {features.filtre_client_aleo && (
                    <>
                        <MultiSelectFilter
                            label="Client (tiers)"
                            endpoint="/api/emis/filters/customers"
                            selected={selectedCustomerTiers}
                            onChange={setSelectedCustomerTiers}
                        />
                        <MultiSelectFilter
                            label="Client (catégorie)"
                            endpoint="/api/emis/filters/customer-codes"
                            selected={selectedCustomers}
                            onChange={setSelectedCustomers}
                        />
                    </>
                )}
            </div>

            {/* Tabs */}
            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="border-b border-gray-200">
                    <nav className="flex overflow-x-auto px-2" aria-label="Onglets">
                        {tabs.map((tab) => {
                            const isActive = activeTab.id === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => navigate(tabPath(tab.relPath))}
                                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                        isActive
                                            ? 'border-brand-600 text-brand-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Sub-tabs — pills */}
                {activeTab.subTabs && (
                    <div className="px-4 pt-3 pb-1">
                        <nav className="inline-flex gap-1 bg-gray-100 rounded-lg p-0.5">
                            {activeTab.subTabs.map((sub) => {
                                const isActiveSub = activeSubTab?.id === sub.id;
                                return (
                                    <button
                                        key={sub.id}
                                        onClick={() => navigate(tabPath(sub.relPath))}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                            isActiveSub
                                                ? 'bg-white text-gray-900 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        {sub.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                )}

                {/* Tab content */}
                <div className="p-4">
                    {activeTab.id === 'marge-brute' && driver !== null && (
                        driver === 'alia-categorization'
                            ? <MargeBruteAlia filters={filters} />
                            : <MargeBrute filters={filters} />
                    )}
                    {activeTab.id === 'suivi-reglements' && (
                        <SuiviReglements filters={filters} variant={(activeSubTab?.id as 'client' | 'prestataire') ?? 'client'} />
                    )}
                    {activeTab.id === 'delais-reglement' && (
                        <DelaisReglement filters={filters} variant={(activeSubTab?.id as 'client' | 'prestataire') ?? 'client'} />
                    )}
                    {activeTab.id === 'delais-enregistrement' && <DelaisEnregistrement filters={filters} />}
                    {activeTab.id === 'delais-traitement' && <DelaisTraitement filters={filters} />}
                    {activeTab.id === 'refacturation' && <Refacturation filters={filters} />}
                </div>
            </div>
        </div>
    );
}
